<template>
  <LandingBackground>
    <div class="mb-20">
      <div class="mx-auto mt-10 grid grid-cols-1 items-start gap-10 sm:px-5 md:px-8 xl:grid-cols-2 3xl:grid-cols-3">
        <div class="flex min-w-0 flex-col gap-10">
          <LandingCard variant="info" :title="`Welcome, ${userRef.firstName} ${userRef.lastName}`">
            <template #default>
              <LandingCardLinkSections label="Grants">
                <LandingCardLinkSection
                  link-label="Find Grants"
                  description="Find, view, and manage grants."
                  :to="{ name: routeNames.grant.list }"
                />
              </LandingCardLinkSections>
              <LandingCardLinkSections label="Contracts">
                <LandingCardLinkSection
                  link-label="Find Contracts"
                  description="Find, view, and manage contracts."
                  :to="{ name: routeNames.contract.list }"
                />
              </LandingCardLinkSections>
              <LandingCardLinkSections label="Projects">
                <LandingCardLinkSection
                  link-label="Find Projects"
                  description="Find, view, and manage projects."
                  :to="{ name: routeNames.project.list }"
                />
              </LandingCardLinkSections>
              <LandingCardLinkSections label="Accounts">
                <LandingCardLinkSection
                  link-label="Find Accounts"
                  description="Find, view, and manage accounts."
                  :to="{ name: routeNames.account.list }"
                />
              </LandingCardLinkSections>
            </template>
          </LandingCard>
          <LandingCard title="Analytics">
            <template #default>
              <div class="py-5 pl-4 sm:pl-6">
                <PublicLandingList>
                  <PublicLandingListItem>
                    <router-link
                      class="text-sky-600 hover:underline"
                      :to="{
                        name: routeNames.analytics.projectDataDownload,
                      }"
                    >
                      Download Project Data</router-link
                    >
                  </PublicLandingListItem>
                  <PublicLandingListItem>
                    <router-link
                      class="text-sky-600 hover:underline"
                      :to="{
                        name: routeNames.analytics.billingDetailDownload,
                      }"
                    >
                      Download Billing Detail</router-link
                    >
                  </PublicLandingListItem>
                </PublicLandingList>
              </div>
            </template>
          </LandingCard>
        </div>

        <div class="flex min-w-0 flex-col gap-10 xl:col-start-2 3xl:contents">
          <div class="flex min-w-0 flex-col gap-10 3xl:col-start-2">
            <LandingCard title="Contracts">
              <template #titleSuffix>
                <Badge :murn="LE.ContractStatus.Active" :lookup-format="LookupFormat.description" />
              </template>
              <LandingCardSkeleton v-if="areReportsLoading" />
              <div class="bg-neutral-50 p-10 sm:rounded-b-lg" v-if="!areReportsLoading && !activeContracts?.length">
                <NoneFoundFlag label="No Active Contacts" />
              </div>
              <LandingCardEntityList
                title="Active"
                :items="activeContracts"
                variant="info"
                :view-all-to="{
                  name: routeNames.contract.list,
                  query: { statusMurnIn: [LE.ContractStatus.Active] },
                }"
              >
                <template #default="{ item }">
                  <LandingCardEntityListItem>
                    <template #primaryLabel> {{ item.title }} - {{ item.number }} </template>
                    <template #secondaryLabel>
                      {{ item.vendorName }}
                    </template>
                    <template #additionalInfo>
                      <div
                        class="mt-1.5 text-sm text-neutral-400 md:mt-0 md:self-center md:pr-4 md:text-right"
                        v-if="item.dueDate"
                      >
                        <span class="md:block"
                          >Expiration Date
                          <span class="text-neutral-500">{{ sysDate.shortDateString(item.expirationDate) }}</span></span
                        >
                      </div>
                    </template>
                  </LandingCardEntityListItem>
                </template>
              </LandingCardEntityList>
            </LandingCard>
          </div>
          <div class="flex min-w-0 flex-col gap-10 3xl:col-start-3">
            <LandingCard title="Projects By Status">
              <div class="space-y-3 bg-neutral-200 px-4 py-3 sm:px-6">
                <DashboardDataButtonGroup title="" class="pb-7" v-if="projects && projects.length > 0"
                  ><DashboardDataButton
                    v-for="item in projects"
                    :key="item.statusMurn"
                    :link-to="{ name: routeNames.project.list, query: { statusMurnIn: [item.statusMurn] } }"
                    :description="item.longDescription"
                    :data-value="item.count"
                  >
                    <template #additional>
                      <Badge
                        :murn="item.statusMurn"
                        :label="`$${formatNumber(item.amount, { decimalPlaces: 0 })}`"
                        bordered
                      ></Badge>
                    </template>
                  </DashboardDataButton>
                </DashboardDataButtonGroup>
              </div>
            </LandingCard>
          </div>
        </div>
      </div>
    </div>
  </LandingBackground>
</template>

<script lang="ts">
import type { LandingCardEntity } from "@/components/landing/landingCard";
import LandingCard from "@/components/landing/LandingCard.vue";
import LandingCardEntityList from "@/components/landing/LandingCardEntityList.vue";
import LandingCardEntityListItem from "@/components/landing/LandingCardEntityListItem.vue";
import LandingCardLinkSection from "@/components/landing/LandingCardLinkSection.vue";
import LandingCardLinkSections from "@/components/landing/LandingCardLinkSections.vue";
import LandingCardSkeleton from "@/components/landing/LandingCardSkeleton.vue";
import LandingBackground from "@/components/LandingBackground.vue";
import { LE } from "@/constants/enums";
import { routeNames } from "@/routes";
import { getContracts, getProjects } from "@/services/endpoints";
import type { Contract } from "@/views/contract/contract";
import contractService from "@/views/contract/contractService";
import projectService from "@/views/project/projectService";
import { NoneFoundFlag, SingleSpace, useAppSettingsStore } from "@modernary/mui/components/common";
import { adaptPagedResponse, LookupDescription } from "@modernary/mui/components/lookup";
import {
  PublicLandingCardSection,
  PublicLandingList,
  PublicLandingListItem,
} from "@modernary/mui/components/publicLanding";
import { useToastStore } from "@modernary/mui/components/toasts";
import { useApi, type Paging } from "@modernary/mui/utils/api";
import { useUser } from "@modernary/mui/utils/authentication";
import { formatNumber, sysDate } from "@modernary/mui/utils/filters";
import { AxiosError } from "axios";
import { computed, defineComponent, ref, watch } from "vue";
import { InitialBadge, Badge, BadgeColor } from "@modernary/mui/components/badge";
import { BaseButton } from "@modernary/mui/components/button";
import { MdrnAttribution, SkeletonButton, useAppNav } from "@modernary/mui/components/common";
import DashboardDataButtonGroup from "@/components/DashboardDataButtonGroup.vue";
import DashboardDataButton from "@/components/DashboardDataButton.vue";
import { LookupFormat } from "@modernary/mui/components/read";

export default defineComponent({
  name: "HomeInternal",
  components: {
    LandingBackground,
    LandingCard,
    LandingCardLinkSection,
    LandingCardLinkSections,
    LandingCardEntityList,
    LandingCardEntityListItem,
    LandingCardSkeleton,
    LookupDescription,
    NoneFoundFlag,
    PublicLandingList,
    PublicLandingListItem,
    PublicLandingCardSection,
    SingleSpace,
    Badge,
    DashboardDataButtonGroup,
    DashboardDataButton,
    BaseButton,
    InitialBadge,
    MdrnAttribution,
    SkeletonButton,
  },
  setup() {
    const { userRef } = useUser();
    const appSettingsStore = useAppSettingsStore();
    const toastStore = useToastStore();
    const areReportsLoading = ref(1);

    const activeContracts = ref<LandingCardEntity<Contract>[]>();
    getContracts({
      statusMurnIn: [LE.ContractStatus.Active],
      orderBy: [{ propertyName: "title", isDescending: false }],
      take: 5,
    })
      .then(async (response) => {
        const data = (await adaptPagedResponse(response, contractService.responseAdapter as any)) as unknown as Paging<
          LandingCardEntity<Contract>
        >;
        data._items.forEach((item) => {
          item.to = { name: routeNames.contract.view, params: { id: item.id } };
        });
        activeContracts.value = data._items;
      })
      .catch((error) => {
        if (error instanceof AxiosError) {
          toastStore.toastError((options) => {
            options.title = "Could not get Contracts";
          }, error.status || 500);
        }
      })
      .finally(() => {
        areReportsLoading.value--;
      });

    const projects = ref();

    const { exec: fetchProjects } = useApi(getProjects, {
      responseAdapter: async (response) => {
        return await adaptPagedResponse(response, projectService.responseAdapter);
      },
    });

    const projectStatusMap = new Map();
    const getProjectsByStatus = () => {
      fetchProjects().then((result) => {
        if (!result) return;
        result._items.forEach((item) => {
          let status = projectStatusMap.get(item.statusMurn);
          if (!status) {
            status = {
              id: "1",
              longDescription: item.status?.description,
              statusMurn: item.statusMurn,
              amount: 0,
              count: 0,
            };
            projectStatusMap.set(item.statusMurn, status);
          }
          status.amount += item.budgetAmount;
          status.count += 1;
        });

        //Format amounts here before sending up there or keep formatting up there?
        projects.value = Array.from(projectStatusMap.values());
      });
    };

    getProjectsByStatus();

    return {
      activeContracts,
      userRef,
      routeNames,
      appSettingsStore,
      sysDate,
      areReportsLoading,
      LE,
      projects,
      formatNumber,
      LookupFormat,
    };
  },
});
</script>
